import { cn } from "@/lib/utils";
import type { OrderListItemDTO } from "@/modules/orders/domain/types";
import { Skeleton } from "@/components/kit";
import { OrderKanbanCard } from "./OrderKanbanCard";

type KanbanColumnProps = {
  title: string;
  count: number;
  tone: "primary" | "secondary" | "leaf";
  orders: OrderListItemDTO[];
  isLoading?: boolean;
  canManage: boolean;
  onView: (id: string) => void;
  onMarkReady?: (id: string) => void;
  onMarkShipped?: (id: string, tracking: string | null) => void;
  onMarkDelivered?: (id: string) => void;
  onMarkPickedUp?: (id: string) => void;
  markReadyId?: string | null;
  markShippedId?: string | null;
  markDeliveredId?: string | null;
  markPickedUpId?: string | null;
};

const toneStyles = {
  primary: "border-primary/25 bg-primary/5",
  secondary: "border-secondary/30 bg-secondary/10",
  leaf: "border-leaf/30 bg-leaf/10",
} as const;

const toneBadge = {
  primary: "bg-primary text-primary-foreground",
  secondary: "bg-secondary text-secondary-foreground",
  leaf: "bg-leaf text-leaf-foreground",
} as const;

export function KanbanColumn({
  title,
  count,
  tone,
  orders,
  isLoading,
  canManage,
  onView,
  onMarkReady,
  onMarkShipped,
  onMarkDelivered,
  onMarkPickedUp,
  markReadyId,
  markShippedId,
  markDeliveredId,
  markPickedUpId,
}: KanbanColumnProps) {
  return (
    <section
      className={cn(
        "flex min-h-[280px] flex-col gap-3 rounded-2xl border p-3 sm:p-4",
        toneStyles[tone],
      )}
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className="font-display text-foreground text-lg">{title}</h3>
        <span
          className={cn(
            "rounded-full px-2.5 py-0.5 font-medium text-sm tabular-nums",
            toneBadge[tone],
          )}
        >
          {count}
        </span>
      </header>

      <div className="flex flex-1 flex-col gap-3">
        {isLoading ? (
          ["a", "b", "c"].map((id) => (
            <Skeleton key={id} className="h-36 rounded-xl" />
          ))
        ) : orders.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground text-sm">
            ບໍ່ມີອໍເດີ້
          </p>
        ) : (
          orders.map((order) => (
            <OrderKanbanCard
              key={order.id}
              order={order}
              canManage={canManage}
              onView={onView}
              onMarkReady={onMarkReady}
              onMarkShipped={onMarkShipped}
              onMarkDelivered={onMarkDelivered}
              onMarkPickedUp={onMarkPickedUp}
              isMarkReadyPending={markReadyId === order.id}
              isMarkShippedPending={markShippedId === order.id}
              isMarkDeliveredPending={markDeliveredId === order.id}
              isMarkPickedUpPending={markPickedUpId === order.id}
            />
          ))
        )}
      </div>
    </section>
  );
}
